import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { OnOfficeSessionEndpoint, PropstackSessionEndpoint, getApiUrl } from 'constants/API'

import { embedSession } from './embedSession'

interface RetriableConfig { __embedSessionRetried?: boolean }

let installed = false

/**
 * Only replay a 401 with the embed bearer when the request targets the bkbn API. The embed 401
 * interceptor lives on the GLOBAL axios, so a cross-origin call routed through it (e.g. a signed
 * GCS URL fetched with `ignoreBaseUrl`/`ignoreAuthorizationHeaders`) must NOT have the bkbn embed
 * token attached and replayed to that third-party origin. The app builds absolute URLs, so the
 * primary check is a prefix match on `getApiUrl()`; the relative-path branch is a defensive guard
 * for a config that ever resolves against the bkbn baseURL instead.
 */
const isBkbnApiRequest = (config: InternalAxiosRequestConfig): boolean => {
  const apiUrl = getApiUrl()
  const url = config.url ?? ''
  if (url.startsWith(apiUrl)) return true
  const baseURL = config.baseURL
  if (url.startsWith('/')) return baseURL === undefined || baseURL.startsWith(apiUrl)
  return false
}

/**
 * The session endpoints themselves must never be refresh-retried: a 401 from REFRESH re-entering
 * the interceptor would await the SAME single-flight refresh promise — a circular wait that never
 * settles. `embedSession` already bypasses the interceptor via its own axios instance; this guard
 * keeps a future refactor from reintroducing the loop.
 */
const SESSION_ENDPOINT_PATHS: string[] = [
  PropstackSessionEndpoint.EXCHANGE,
  PropstackSessionEndpoint.REFRESH,
  OnOfficeSessionEndpoint.EXCHANGE,
  OnOfficeSessionEndpoint.REFRESH,
]

const isSessionEndpointUrl = (url?: string): boolean =>
  url !== undefined && SESSION_ENDPOINT_PATHS.some((path) => url.includes(path))

/**
 * Reactive fallback to the proactive refresh: a request that raced the access-token expiry gets
 * one refresh + one replay. A second 401 (or a failed refresh) surfaces to the caller — the
 * embed error state takes over from there. A 5xx from the refresh/exchange propagates unretried
 * (the token may already be burned server-side, so a retry cannot recover it).
 */
export const installEmbedSessionInterceptor = (): void => {
  if (installed) return
  installed = true
  axios.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as (typeof error.config & RetriableConfig) | undefined
    const isRetriable = error.response?.status === 401 &&
      embedSession.isActive() &&
      config !== undefined &&
      !config.__embedSessionRetried &&
      !isSessionEndpointUrl(config.url) &&
      isBkbnApiRequest(config)
    if (!isRetriable) throw error
    await embedSession.refresh()
    config.__embedSessionRetried = true
    const token = await embedSession.getAccessToken()
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` } as typeof config.headers
    return axios.request(config)
  })
}

/** Test-only: resets the install latch so idempotency can be exercised in isolation. */
export const _resetInterceptorForTests = () => {
  installed = false
}
